import { makeAutoObservable } from 'mobx'
import {
  type IRowBase,
  type ITableSortOrder,
  type ITableRequestParams,
  type ITableSortBy,
  type IRowDefault,
} from 'shared/ui/TableNew'
import { TableColumnsStore } from 'shared/ui/TableNew/store/tableColumnsStore'
import { TableBulkStore } from 'shared/ui/TableNew/store/tableBulkStore'

type ITableStoreConfig<TRow extends IRowBase> = {
  key: string
  customSelectAllOnPageIds?: () => void
  element?: string
  filterRowsFn?: (row: TRow) => boolean
  saveColumns?: (columns: string[]) => Promise<string[]> | string[]
  getColumns?: () => Promise<string[]> | string[]
  onChangeColumns?: (columns: string[], columnsStore: TableColumnsStore<TRow>) => void
  useColumnsCache?: boolean
  selectAllCallback?: () => Promise<void>
  resetSelectAllCallback?: () => void
  getLimit?: () => number | undefined
  setLimit?: (value: number) => void

  page?: number
  limit?: number
  sortBy?: ITableSortBy
  sortOrder?: ITableSortOrder
  onSort?: (field: string) => void
  usePaginationPage?: boolean
}

export class TableStore<TRow extends IRowDefault = IRowDefault> {
  private _rowsMap: Map<number | string, TRow> = new Map()

  private _loading = false

  private _page = 1
  private _total = 0
  private _search = ''

  private _startLimit = 25
  private _limit = 25
  private _hasMore: boolean | null = null

  // Manage columns
  private readonly _tableColumnsStore: TableColumnsStore<TRow>

  // Sorting
  private _sortOrder: ITableSortOrder = 'desc'
  private _sortBy: ITableSortBy = ''

  // Bulk action
  private readonly _tableBulkStore: TableBulkStore<TRow>

  private readonly _config: ITableStoreConfig<TRow>

  constructor(config: ITableStoreConfig<TRow>) {
    makeAutoObservable(this)

    this._config = config
    this._page = config?.page || this._page
    this._limit = config.getLimit?.() ?? config?.limit ?? this._limit
    this._startLimit = config?.limit ?? this._startLimit
    this._sortBy = config?.sortBy || this._sortBy
    this._sortOrder = config?.sortOrder || this._sortOrder
    this._tableColumnsStore = new TableColumnsStore(this)
    this._tableBulkStore = new TableBulkStore(this)
  }

  get columnsStore() {
    return this._tableColumnsStore
  }

  get bulkStore() {
    return this._tableBulkStore
  }

  get selectedIds() {
    return this._tableBulkStore.selectedIds
  }

  get selectedRows() {
    return this._tableBulkStore.selectedRows
  }

  get bulkAllMode() {
    return this._tableBulkStore.bulkAllMode
  }

  get bulkTotal() {
    return this._tableBulkStore.bulkTotal
  }

  get visibleColumnsIds() {
    return this._tableColumnsStore.visibleColumnsIds
  }

  get config() {
    return this._config
  }

  get element() {
    return this._config?.element || ''
  }

  get loading() {
    return this._loading
  }

  get limit() {
    return this._limit
  }

  get startLimit() {
    return this._startLimit
  }

  get rows() {
    return Array.from(this._rowsMap.values())
  }

  get rowsIds() {
    return this.rows.map((row) => row.id)
  }

  get total() {
    return this._total
  }

  get search() {
    return this._search
  }

  get sortOrder() {
    return this._sortOrder
  }

  get sortBy() {
    return this._sortBy
  }

  get page() {
    return this._page
  }

  get hasMore() {
    return this._hasMore
  }

  get params(): ITableRequestParams {
    return {
      page: this._page,
      length: this._limit,
      search: this._search,
      sortOrder: this._sortOrder,
      sortBy: [this._sortBy],
    }
  }

  get allVisibleRowsSelected() {
    const selectableRows = this.rows.filter((row) => !row.isDisabled)
    return (
      selectableRows.length > 0 && selectableRows.every((row) => this.selectedIds.includes(row.id))
    )
  }

  dispose = () => {
    this._page = 1
    this._limit = this._config.getLimit?.() ?? this._startLimit
    this._search = ''
    this._sortOrder = 'desc'
    this._sortBy = ''
    this._loading = false
    this._total = 0
    this.resetSelected()
  }

  setRows = (rows: TRow[]) => {
    this._rowsMap.clear()

    let items = []

    if (this._config?.filterRowsFn) {
      items = rows.filter(this._config.filterRowsFn)
    } else {
      items = rows.filter((row) => !row.isDisabled && !row.isCheckboxDisabled)
    }

    items.forEach((row) => {
      this._rowsMap.set(row.id, row)
    })
  }

  setHasMore = (value: boolean | null) => {
    this._hasMore = value
  }

  setVisibleColumnsIds = (ids: string[]) => {
    this._tableColumnsStore.setVisibleColumnsIds(ids)
  }

  toggleSortOrder = () => {
    this._sortOrder = this._sortOrder === 'desc' ? 'asc' : 'desc'
  }

  private changeSortBy = (field: ITableSortBy) => {
    if (this._sortBy !== field) {
      this._sortBy = field
      this._sortOrder = 'desc'
    }
  }

  onSort = (field: string) => {
    this.toggleSortOrder()
    this.changeSortBy(field)
    this._config?.onSort?.(field)
  }

  onRowSelect = (row: TRow, isSelected: boolean, isShiftKey = false) => {
    this._tableBulkStore.onRowSelect(row, isSelected, isShiftKey)
  }

  setTotal = (value: number) => {
    this._total = value
    this.setBulkTotal(value)
  }

  setPage = (value: number) => {
    this._page = value
  }

  setLimit = (value: number) => {
    this._limit = value
    this._config.setLimit?.(value)
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setSortOrder = (value: ITableSortOrder) => {
    this._sortOrder = value
  }

  setSortBy = (value: ITableSortBy) => {
    this._sortBy = value
  }

  setBulkTotal = (value: number) => {
    this._tableBulkStore.setBulkTotal(value)
  }

  resetSelected = () => {
    this._tableBulkStore.resetSelected()
  }

  unselectAllIds = () => {
    this._tableBulkStore.unselectAllIds()
  }

  setSearch = (value: string) => {
    this._search = value
  }
}
